import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { ICellRendererParams, IRowNode } from 'ag-grid-community';
import classNames from 'classnames';
import { Dropdown, Table } from 'react-bootstrap';
import ChildrensTooltip from '@/components/common/ChildrensTooltip';
import Divider from '@/components/common/Divider';
import SubtleBadge from '@/components/common/SubtleBadge';
import { difficulyRanges, serpDifficuly } from '@/helpers/serpDifficulity';
import {
  intentMap
} from '@/app/KeywordResearch/config/keywordGapColumnConfig';
import { successMessage } from '@/utils/toast';
import { decodeName } from '../utils/decodeName';
import { copyText } from '../utils/download';
import {
  aggregateSeoFromLeaves,
  formatVolume,
  mapPurpose,
  phraseWordCount
} from '../utils/seoMetrics';
import type { ClusterSeoMetrics } from '../types';

type ClusterTreeRow = {
  name: string[];
  volume: number | null;
  difficulty: number | null;
  len: number | null;
  intent: string[];
  purpose: string[];
};

/** Resolve leaf SEO or aggregate parent from all leaf descendants. */
function getRowSeoMetrics(
  params: ICellRendererParams<ClusterTreeRow>
): Partial<ClusterSeoMetrics> | null {
  const node = params.node;
  if (!node) return null;

  if (!node.group) {
    const data = params.data;
    if (!data) return null;
    return {
      search_volume_miztols: data.volume ?? 0,
      difficulty: data.difficulty,
      len: data.len,
      intent: data.intent || [],
      purpose: data.purpose || []
    };
  }

  const leaves = (node.allLeafChildren || [])
    .map(child => child.data)
    .filter((data): data is ClusterTreeRow => Boolean(data))
    .map(data => ({
      search_volume_miztols: data.volume ?? 0,
      difficulty: data.difficulty,
      len: data.len,
      intent: data.intent || [],
      purpose: data.purpose || []
    }));

  return aggregateSeoFromLeaves(leaves);
}

function getRowLength(params: ICellRendererParams<ClusterTreeRow>): number | null {
  const node = params.node;
  if (!node) return null;

  if (node.group) {
    const label = node.key ?? node.groupData?.['ag-Grid-AutoColumn'];
    return label ? phraseWordCount(label) : null;
  }

  return params.data?.len ?? null;
}

function collectClusterKeywords(node: IRowNode<ClusterTreeRow> | null | undefined): string[] {
  const leaves = node?.allLeafChildren || [];
  const keywords: string[] = [];
  const seen = new Set<string>();

  leaves.forEach(leaf => {
    const path = leaf.data?.name || [];
    const keyword = decodeName(path[path.length - 1] || '');
    if (!keyword || seen.has(keyword)) return;
    seen.add(keyword);
    keywords.push(keyword);
  });

  return keywords;
}

function ClusterActionsCell({
  node
}: {
  node: IRowNode<ClusterTreeRow> | null | undefined;
}) {
  const hasChildren =
    Boolean(node?.group) || (node?.allChildrenCount ?? 0) > 0;

  if (!hasChildren) return null;

  return (
    <div className="w-100 d-flex h-100 align-items-center justify-content-center">
      <span className="allaho_akbar_zIndex">
        <Dropdown
          onClick={e => e.stopPropagation()}
          align="start"
          className="allaho_akbar_zIndex overflow-visible btn-reveal-trigger d-inline-block me-0"
        >
          <Dropdown.Toggle
            className="text-600 btn-reveal"
            split
            variant="reveal"
            size="sm"
          >
            <FontAwesomeIcon icon="ellipsis-h" className="fs--2" />
          </Dropdown.Toggle>
          <Dropdown.Menu
            className="border py-0"
            renderOnMount
            popperConfig={
              { strategy: 'fixed', placement: 'bottom-start' } as never
            }
          >            <Dropdown.Item
              onClick={async () => {
                const keywords = collectClusterKeywords(node);
                if (!keywords.length) return;
                await copyText(keywords.join('\n'));
                successMessage(`${keywords.length} کلمه کپی شد`);
              }}
              className={classNames(
                'link-600 fs--1 d-flex align-items-center gap-1 p-0 px-1'
              )}
            >
              <FontAwesomeIcon icon="copy" className="me-1 ms-2" />
              کپی کلمات
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </span>
    </div>
  );
}

function DifficultyCell({ value }: { value: number | null | undefined }) {
  if (value == null) return '—';
  const current = serpDifficuly(value);

  return (
    <div className="d-flex align-items-center justify-content-center">
      <ChildrensTooltip
        placement="top"
        html={
          <div>
            سختی کلمه کلیدی
            <Divider />
            <Table borderless size="sm">
              <tbody>
                {difficulyRanges.map(([start, end]) => (
                  <tr
                    className={classNames({
                      'fw-black fs--1 position-relative':
                        serpDifficuly(start).color === current.color
                    })}
                    key={start}
                  >
                    <td className="d-flex align-items-center gap-1">
                      {serpDifficuly(start).color === current.color && (
                        <FontAwesomeIcon
                          style={{
                            right: 3,
                            top: '50%',
                            transform: 'translateY(-50%)'
                          }}
                          icon="chevron-left"
                          className="position-absolute text-primary"
                          size="sm"
                        />
                      )}
                      <span
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          direction: 'ltr',
                          height: '20px',
                          width: '28px',
                          fontSize: '12px',
                          backgroundColor: serpDifficuly(start).color
                        }}
                        className="fs--2 px-2 py-0.5 text-white rounded-2"
                      >
                        {start}
                      </span>
                      <FontAwesomeIcon icon="minus" />
                      <span
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          direction: 'ltr',
                          height: '20px',
                          width: '28px',
                          fontSize: '12px',
                          backgroundColor: serpDifficuly(start).color
                        }}
                        className="fs--2 px-2 py-0.5 text-white rounded-2"
                      >
                        {end}
                      </span>
                    </td>
                    <td className="text-white">{serpDifficuly(start).label}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        }
      >
        <div className="fs-1 rounded-2 w-full" style={{ fontWeight: 'bold' }}>
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              direction: 'ltr',
              marginTop: '6px',
              height: '20px',
              width: '28px',
              fontSize: '12px',
              backgroundColor: current.color
            }}
            className="fs--2 px-2 py-0.5 text-white rounded-2"
          >
            {value}
          </span>
        </div>
      </ChildrensTooltip>
    </div>
  );
}

/** Extra SEO columns for AgGridTree (paths / tree table). */
export const clusterTreeSeoColumnDefs = [
  {
    field: 'actions',
    headerName: ' ',
    width: 52,
    maxWidth: 52,
    minWidth: 52,
    flex: 0,
    pinned: 'left',
    sortable: false,
    suppressSizeToFit: true,
    resizable: false,
    cellClass: 'over-flow',
    cellStyle: { textAlign: 'center', margin: '0px' },
    cellRenderer: (params: ICellRendererParams<ClusterTreeRow>) => (
      <ClusterActionsCell node={params.node} />
    )
  },
  {
    field: 'volume',
    headerName: 'میزان جستجو',
    flex: 1,
    minWidth: 110,
    sortable: true,
    cellRenderer: (params: ICellRendererParams<ClusterTreeRow>) => {
      const seo = getRowSeoMetrics(params);
      if (!seo || seo.search_volume_miztols == null) return '—';
      return formatVolume(seo.search_volume_miztols);
    }
  },
  {
    field: 'difficulty',
    headerName: 'سختی',
    width: 96,
    maxWidth: 96,
    minWidth: 96,
    flex: 0,
    suppressSizeToFit: true,
    sortable: true,
    tooltip:
      'هرچه مقدار بالاتر باشد، کسب رتبه در بین ۱۰ نتیجه اول گوگل دشوارتر خواهد بود.',
    cellRenderer: (params: ICellRendererParams<ClusterTreeRow>) => (
      <DifficultyCell value={getRowSeoMetrics(params)?.difficulty} />
    )
  },
  {
    field: 'len',
    headerName: 'طول',
    width: 80,
    maxWidth: 80,
    minWidth: 80,
    flex: 0,
    suppressSizeToFit: true,
    sortable: true,
    cellRenderer: (params: ICellRendererParams<ClusterTreeRow>) => {
      const len = getRowLength(params);
      return len != null ? len : '—';
    }
  },
  {
    field: 'intent',
    headerName: 'نیت',
    flex: 1.2,
    minWidth: 120,
    sortable: false,
    tooltip: (
      <div className="text-start">
        نیت جستجو (Search Intent) یعنی قصد کاربر از جستجوی یک کلمه کلیدی چیه؟
        داده‌های این ستون از هوش مصنوعی گرفته می‌شود.
      </div>
    ),
    cellRenderer: (params: ICellRendererParams<ClusterTreeRow>) => {
      const intent = getRowSeoMetrics(params)?.intent || [];
      if (!intent.length) return '—';
      return (
        <div className="w-100 d-flex h-100 align-items-center justify-content-start gap-2">
          {intent.map((code, i) => (
            <ChildrensTooltip
              placement="top"
              key={`${code}-${i}`}
              delayToRemove={0}
              html={intentMap[code as keyof typeof intentMap]?.tooltip}
            >
              <SubtleBadge
                pill
                bg={intentMap[code as keyof typeof intentMap]?.variant}
                className="d-flex align-items-center gap-1 fs--2"
              >
                {code}
              </SubtleBadge>
            </ChildrensTooltip>
          ))}
        </div>
      );
    }
  },
  {
    field: 'purpose',
    headerName: 'هدف',
    flex: 1.5,
    minWidth: 140,
    sortable: false,
    tooltip:
      'پیشنهاد می‌دهد که کلمه برای چه نوع محتوایی مناسب‌تر است. داده‌های این بخش از هوش مصنوعی گرفته می‌شود.',
    cellRenderer: (params: ICellRendererParams<ClusterTreeRow>) => {
      const purpose = getRowSeoMetrics(params)?.purpose || [];
      if (!purpose.length) return '—';
      return (
        <div className="w-100 d-flex h-100 align-items-center justify-content-start gap-2">
          {purpose.map((item, i) => (
            <SubtleBadge
              pill
              key={`${item}-${i}`}
              bg="info"
              className="d-flex align-items-center gap-1 fs--2"
            >
              {mapPurpose(item)}
            </SubtleBadge>
          ))}
        </div>
      );
    }
  }
];
